import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Bot, Shield } from 'lucide-react'
import { sanitize } from '@/lib/html'
import { renderChatMarkdown } from '@/features/chatbot/markdown'
import { PartsRenderer } from '@/features/chatbot/renderers/PartsRenderer'
import type { ChatCitation, ChatMessagePart } from '@/features/chatbot/types'

interface SharedMessage {
  id: string
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  parts?: ChatMessagePart[] | null
  citations?: ChatCitation[] | null
  created_at: string
}

interface SharedThread {
  kind: 'thread'
  title: string
  tenant: string
  created_at: string
  shared_at: string
  messages: SharedMessage[]
}

interface SharedPart {
  kind: 'part'
  title: string
  tenant: string
  shared_at: string
  prompt: string | null
  part: ChatMessagePart
}

type SharedPayload = SharedThread | SharedPart

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: SharedPayload }
  | { kind: 'error'; message: string }

/**
 * Public read-only view for a shared chat. The payload shape is one of:
 *   - { kind: 'thread', messages: [...] } - full transcript
 *   - { kind: 'part', part: {...}, prompt: '...' } - single table/chart/artifact
 * No auth, no app chrome - access is the unguessable share token.
 */
export function SharedChatPage() {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setState({ kind: 'error', message: 'Invalid share link.' })
      return
    }
    const load = async (): Promise<void> => {
      try {
        const res = await fetch(`/api/chat-shares/${encodeURIComponent(token)}`, {
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        })
        if (cancelled) return
        if (res.status === 404) {
          setState({
            kind: 'error',
            message: 'This share link was revoked, expired, or never existed.',
          })
          return
        }
        if (!res.ok) {
          setState({ kind: 'error', message: `Failed to load shared chat (HTTP ${res.status}).` })
          return
        }
        const data = (await res.json()) as SharedPayload
        setState({ kind: 'ok', data })
      } catch (e) {
        if (cancelled) return
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : 'Network error.',
        })
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [token])

  if (state.kind === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading shared chat…</div>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-sm">
          <Shield className="mx-auto mb-3 size-6 text-muted-foreground" />
          <div className="text-sm font-semibold text-foreground">Link unavailable</div>
          <div className="mt-1 text-xs text-muted-foreground">{state.message}</div>
        </div>
      </div>
    )
  }

  const chat = state.data
  // For single-part shares (shared table/chart), the thread title is
  // derived from the asker's prompt. Surfacing it on the public page
  // would leak the question - keep only the workspace + date line.
  const isPartShare = chat.kind === 'part'

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="min-w-0">
            {isPartShare ? (
              <div className="truncate text-sm font-semibold text-foreground">
                Shared from {chat.tenant}
              </div>
            ) : (
              <div className="truncate text-base font-semibold text-foreground sm:text-lg">
                {chat.title}
              </div>
            )}
            <div className="truncate text-[11px] text-muted-foreground">
              {isPartShare
                ? new Date(chat.shared_at).toLocaleDateString()
                : `Shared from ${chat.tenant} · ${new Date(chat.shared_at).toLocaleDateString()}`}
            </div>
          </div>
          <div className="hidden shrink-0 items-center gap-1 rounded-full border border-border bg-muted px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:inline-flex">
            <Shield className="size-3" /> Read-only share
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        {chat.kind === 'part' ? (
          <SharedPartView data={chat} />
        ) : chat.messages.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">
            This chat has no messages.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {chat.messages.map((m) => (
              <SharedMessageView key={m.id} message={m} />
            ))}
          </div>
        )}
      </main>
      <footer className="border-t border-border py-6 text-center text-[11px] text-muted-foreground">
        Powered by Bokapsys · This view is read-only and doesn&apos;t require a login.
      </footer>
    </div>
  )
}

function SharedMessageView({ message: m }: { message: SharedMessage }) {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[72%] rounded-2xl bg-muted px-4 py-2 text-sm leading-relaxed text-foreground">
          {m.content}
        </div>
      </div>
    )
  }
  const parts = (m.parts as ChatMessagePart[] | null) ?? null
  const hasTextSegments = Array.isArray(parts) && parts.some((p) => p.kind === 'text')
  return (
    <div className="flex items-start gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary-gradient text-primary-foreground">
        <Bot className="size-4" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5 text-sm leading-relaxed text-foreground">
        {hasTextSegments ? (
          <PartsRenderer parts={parts} />
        ) : (
          <>
            <div
              className="prose-sm max-w-none [&_a]:text-primary [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_li]:my-1 [&_p]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
              // eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in lib/html.ts
              dangerouslySetInnerHTML={{ __html: sanitize(renderChatMarkdown(m.content || '')) }}
            />
            <PartsRenderer parts={parts} />
          </>
        )}
      </div>
    </div>
  )
}

function SharedPartView({ data }: { data: SharedPart }) {
  // Intentionally does not render `data.prompt` - the single-part share
  // is meant to stand on its own (a table or chart) without exposing the
  // question that produced it.
  return (
    <div className="space-y-4">
      <PartsRenderer parts={[data.part]} />
    </div>
  )
}
